import React, { useEffect, useContext, Fragment } from 'react';
import { string, shape, bool } from 'prop-types';
import { Col } from '@thd-olt-component-react/grid';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Carousel } from '@thd-olt-component-react/carousel';
import {
  arrayOf as arrayType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { FullCard } from './FullCard';
import { GuideZoneCard } from './GuideZoneCard';
import { RentalZoneCard } from './RentalZoneCard';
import { InstallZoneCard } from './InstallZoneCard';
import { getCardType } from './Helpers';
import Model from './model/Model';
import './category-cards.scss';

const CategoryCards = ({
  keyword,
  navParam,
  storeId,
  data: propData,
  loading: propLoading,
  error: propError,
  ssr
}) => {

  if (!keyword && !navParam) {
    if (typeof propLoading === 'undefined') {
      return null;
    }
  }

  const { channel, store, clientStore } = useContext(ExperienceContext);
  const { storeId: defaultStoreId } = clientStore || store;

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('category-cards.ready'); }, []);

  /* eslint-disable-next-line react/prop-types */
  const renderCardByType = ({ header, details, type, major }) => {
    switch (type) {
    case 'guide': return <GuideZoneCard channel={channel} major={major} header={header} details={details} />;
    case 'rental': return <RentalZoneCard channel={channel} major={major} header={header} details={details} />;
    case 'install': return <InstallZoneCard channel={channel} major={major} header={header} details={details} />;
    default: return null;
    }
  };

  const renderFull = (categoryCards) => {
    const { majorDetails, majorHeader } = categoryCards;

    if (!majorHeader || !majorDetails) return null;

    const cardType = getCardType(majorHeader.title);

    return (
      <div className="category-cards">
        <Col className="category-cards__zone-wrapper category-cards__full-card">
          <FullCard channel={channel} type={cardType} header={majorHeader} details={majorDetails} />
        </Col>
      </div>
    );
  };

  const renderMultipleCards = (categoryCards) => {
    const { majorDetails, minorDetails, majorHeader, minorHeader } = categoryCards;

    return (
      <>
        {renderCardByType({
          header: majorHeader,
          details: majorDetails,
          type: getCardType(majorHeader.title),
          major: true
        })}
        {renderCardByType({
          header: minorHeader,
          details: minorDetails,
          type: getCardType(minorHeader.title),
          major: false
        })}
      </>
    );
  };

  const getCarouselCards = (carouselCards = []) => {
    return carouselCards.map((carouselCard, i) => {
      return <Fragment key={`card-${i}`}>{carouselCard}</Fragment>;
    });
  };

  const renderMobileCarouselCards = (categoryCards) => {
    const {
      majorDetails,
      minorDetails,
      majorHeader,
      minorHeader
    } = categoryCards;

    const carouselConentCards = [
      renderCardByType({
        header: majorHeader,
        details: majorDetails,
        type: getCardType(majorHeader.title),
        major: true
      }),
      renderCardByType({
        header: minorHeader,
        details: minorDetails,
        type: getCardType(majorHeader.title),
        major: false
      })
    ];

    return (
      <Carousel
        multiItem={false}
        showDots
        dotBelow
        showArrows={false}
      >
        {getCarouselCards(carouselConentCards)}
      </Carousel>
    );
  };

  const renderZone = (categoryCards) => {
    const { majorDetails, minorDetails, majorHeader, minorHeader } = categoryCards;

    if (!majorHeader || !majorDetails || !minorHeader || !minorDetails) return null;

    return (
      <Col className="category-cards">
        <div className="category-cards__zone-wrapper category-cards__zone-card">
          { channel !== 'mobile' ? renderMultipleCards(categoryCards) : renderMobileCarouselCards(categoryCards)}
        </div>
      </Col>
    );
  };

  const variables = {
    storeId: storeId || defaultStoreId,
  };
  if (keyword) {
    variables.keyword = keyword;
  }
  if (navParam) {
    variables.navParam = navParam;
  }
  let { data, loading, error } = useDataModel('searchModel', {
    variables,
    skip: (propData || propLoading || propError),
    ssr
  });

  if (propData || propLoading || propError) {
    data = propData;
    loading = propLoading;
    error = propError;
  }

  if (loading && !data) {
    return <div>Loading...</div>;
  }

  if (error && !data) {
    return <pre><code>{error.toString()}</code></pre>;
  }

  if (!data || !data?.searchModel?.orangeGraph) {
    return null;
  }

  const categoryCards = new Model(data.searchModel.orangeGraph);

  return categoryCards.isFullCard(channel)
    ? renderFull(categoryCards)
    : renderZone(categoryCards);
};

CategoryCards.displayName = 'CategoryCards';

const pod = shapeType({
  title: stringType(),
  description: stringType(),
  imageUrl: stringType(),
  link: stringType(),
});

const universalSearchWithPods = shapeType({
  pods: arrayType(pod),
  info: shapeType({
    title: stringType(),
  })
});

CategoryCards.dataModel = ({
  searchModel: params({ keyword: stringType(), navParam: stringType(), storeId: stringType() }).shape({
    orangeGraph: shapeType({
      universalSearchArray: arrayType(universalSearchWithPods),
      productTypes: arrayType(stringType())
    })
  })
});

CategoryCards.propTypes = {
  keyword: string,
  navParam: string,
  data: shape({}),
  loading: bool,
  error: shape({}),
  ssr: bool
};

CategoryCards.defaultProps = {
  keyword: null,
  navParam: null,
  data: undefined,
  loading: undefined,
  error: undefined,
  ssr: false
};

export { CategoryCards };
